<template>
  <div
    v-if="isVisible"
    class="Countdown"
    role="timer"
    :aria-label="ariaLabel"
  >
    <template
      v-for="(segment, index) of segments"
      :key="segment.unit"
    >
      <span
        v-if="index > 0"
        class="Countdown-separator"
        aria-hidden="true"
      >:</span>
      <div class="Countdown-segment">
        <span class="Countdown-value">{{ segment.displayValue }}</span>
        <span class="Countdown-label">{{ segment.label }}</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useNow } from '@vueuse/core';
import { differenceInSeconds, isAfter, isBefore } from 'date-fns';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import type { CountdownProps, TimeUnit } from './Countdown.model';
import { UNIT_ORDER, UNIT_SECONDS, UNIT_LABEL_KEYS } from './Countdown.model';

const props = withDefaults(defineProps<CountdownProps>(), {
  format: 'HH:MM:SS',
  minChars: 2,
});

const { t } = useI18n();

const { now, pause, resume } = useNow({ interval: 1000, controls: true });

const isVisible = computed(() => {
  const isAfterStart = !props.start || !isBefore(now.value, props.start);
  const isBeforeEnd = !isAfter(now.value, props.end);
  return isAfterStart && isBeforeEnd;
});

watch(isVisible, (visible) => {
  if (visible) resume();
  else pause();
}, { immediate: true });

const activeUnits = computed<TimeUnit[]>(() => UNIT_ORDER.filter((unit) => props.format.includes(unit)));

const totalSeconds = computed(() => Math.max(0, differenceInSeconds(props.end, now.value)));

const segments = computed(() => {
  let remaining = totalSeconds.value;

  return activeUnits.value.map((unit) => {
    const value = Math.floor(remaining / UNIT_SECONDS[unit]);
    remaining = remaining % UNIT_SECONDS[unit];

    return {
      unit,
      displayValue: String(value).padStart(props.minChars, '0'),
      label: t(UNIT_LABEL_KEYS[unit]),
    };
  });
});

const ariaLabel = computed(() => segments.value.map((segment) => `${segment.displayValue} ${segment.label}`).join(', '));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Countdown {
    display: inline-flex;
    align-items: flex-start;
    gap: vars.$dimensions-d-2;

    &-separator {
      @include text.heading(h2);
      line-height: 1;
      align-self: flex-start;
    }

    &-segment {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: vars.$dimensions-d-1;
    }

    &-value {
      @include text.heading(h2);
      gap: vars.$dimensions-d-2;
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }

    &-label {
      @include text.text(sm);
    }
  }
}
</style>
